import React, { useCallback, useMemo } from 'react'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentCheckIcon,
  PresentationChartLineIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline'
import { Transition } from '@headlessui/react'
import { RiskPreviewResult } from '../../../../redux/api/project-risk-api-slice'
import { Divider, Text, Popover } from '@mantine/core'
import { InTextCitation } from '../../../../shared/interfaces/project/document/in-text-citation/in-text-citation.interface'
import { useTabWidth } from '../../../../hooks/use-tab-width'
import { RiskReviewSource } from '../risk-review-card/risk-review-source'
import RiskReviewAnswerTextCitation from '../risk-review-card/risk-review-answer-text-citation'
import { ProjectDocumentMetadata } from '../../../../shared/interfaces/project/document/document.interface'

interface RiskPreviewPanelProps {
  isOpen: boolean
  onToggle: () => void
  isLoading: boolean
  previewData: RiskPreviewResult | null
  error: string | null
  documents: ProjectDocumentMetadata[]
}

interface GroupedEndIndex {
  [index: number]: InTextCitation[]
}

const BoldText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\*\*.*?\*\*)/).map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <span key={index} className="font-semibold">
            {part.slice(2, -2)}
          </span>
        )
      }
      return part
    })}
  </>
)

const RiskPreviewPanel: React.FC<RiskPreviewPanelProps> = ({
  isOpen,
  onToggle,
  isLoading,
  previewData,
  error,
  documents,
}) => {
  const { tabWidth } = useTabWidth()
  const [sourcesExpanded, setSourcesExpanded] = React.useState(false)

  const getRiskLevelConfig = (level: number) => {
    switch (level) {
      case 3: // HIGH
        return {
          color: 'text-red-600 bg-red-50',
          borderColor: 'border-red-200',
          icon: XCircleIcon,
          label: 'High Risk',
        }
      case 2: // MEDIUM
        return {
          color: 'text-yellow-600 bg-yellow-50',
          borderColor: 'border-yellow-200',
          icon: ExclamationTriangleIcon,
          label: 'Medium Risk',
        }
      case 1: // LOW
        return {
          color: 'text-green-600 bg-green-50',
          borderColor: 'border-green-200',
          icon: CheckCircleIcon,
          label: 'Low Risk',
        }
      default: // NO_RANKING (0)
        return {
          color: 'text-gray-600 bg-gray-50',
          borderColor: 'border-gray-200',
          icon: null,
          label: 'Unranked',
        }
    }
  }

  const renderAnswerText = useCallback(
    (answerText: string, projectRiskSources: InTextCitation[]) => {
      if (!projectRiskSources || !answerText) {
        return answerText
      }

      const sortedCitations = projectRiskSources
        .filter(
          (citation) =>
            citation.start_index !== null && citation.end_index !== null
        )
        .sort((a, b) => (a?.start_index ?? 0) - (b?.start_index ?? 0))

      const answerTexts: JSX.Element[] = []
      let lastEndIndex = 0

      const groupedEndIndexes = sortedCitations.reduce<GroupedEndIndex>(
        (acc, citation) => {
          if (acc[citation.end_index ?? 0]) {
            acc[citation.end_index ?? 0].push(citation)
          } else {
            acc[citation.end_index ?? 0] = [citation]
          }
          return acc
        },
        {}
      )

      let total = 0
      for (const groupedIndex of Object.keys(groupedEndIndexes)) {
        const endIndex = parseInt(groupedIndex)
        const citations = groupedEndIndexes[endIndex]
        const textSlice = answerText?.slice(lastEndIndex, endIndex)

        answerTexts.push(
          <span key={`text-${total}`}>
            <BoldText text={textSlice ?? ''} />
          </span>
        )

        for (const citation of citations) {
          answerTexts.push(
            <RiskReviewAnswerTextCitation
              key={`source_${citation.document_segment.quads?.[0].x1}${citation.document_segment.quads?.[0].y1}_${citation.start_index}_${citation.end_index}`}
              index={total}
              citation={citation}
              citations={citations}
            />
          )
          total += 1
        }
        lastEndIndex = endIndex
      }

      const finalTextSlice = answerText?.slice(lastEndIndex)
      answerTexts.push(
        <span key="text-final">
          <BoldText text={finalTextSlice ?? ''} />
        </span>
      )

      return answerTexts
    },
    []
  )

  const onClickSources = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setSourcesExpanded((s) => !s)
  }, [])

  const hasStrikethroughText = useMemo(() => {
    return previewData?.sources?.some(
      (source) => source.document_segment?.metadata?.has_strikethrough_text
    )
  }, [previewData?.sources])

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <div className="flex items-center">
          <h3 className="text-sm font-medium text-gray-900">Risk Preview</h3>
          {previewData && !isLoading && (
            <span
              className={`ml-3 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                getRiskLevelConfig(previewData.risk_ranking || 0).color
              }`}
            >
              {getRiskLevelConfig(previewData.risk_ranking || 0).label}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>

      <Transition
        show={isOpen}
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
      >
        <div className="border-t border-gray-200">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <svg
                className="h-8 w-8 animate-spin text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="ml-3 text-sm text-gray-600">
                Analyzing risk...
              </span>
            </div>
          )}

          {error && !isLoading && (
            <div className="p-4">
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <XCircleIcon className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      Preview Error
                    </h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {previewData && !isLoading && !error && (
            <div className="text-md w-full space-y-2 bg-white px-4">
              <Divider color="#dadada" size="sm" w="100%" />
              <div className="space-y-4 px-2 py-4">
                {/* Analysis Section */}
                <div className="flex justify-between gap-2">
                  <div className="mt-0.5 flex flex-col justify-start">
                    <PresentationChartLineIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="mb-2 flex items-start justify-between">
                      <div className="flex items-center gap-2 text-gray-800">
                        <div className="font-semibold">Analysis</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Sources Dropdown */}
                        <Popover position="bottom-end" offset={12} shadow="lg">
                          <Popover.Target>
                            <div
                              onClick={
                                previewData.sources &&
                                previewData.sources.length > 0
                                  ? onClickSources
                                  : undefined
                              }
                              className={`flex items-center justify-start ${
                                previewData.sources &&
                                previewData.sources.length > 0
                                  ? 'cursor-pointer'
                                  : '!cursor-default'
                              }`}
                            >
                              <DocumentCheckIcon className="mr-1 h-5 w-5 text-gray-400" />
                              <span
                                className={`text-md ${
                                  !previewData.sources ||
                                  previewData.sources.length === 0
                                    ? 'text-gray-400'
                                    : ''
                                }`}
                              >
                                {previewData.sources &&
                                previewData.sources.length > 0
                                  ? 'Sources'
                                  : 'No Sources'}
                              </span>
                              {previewData.sources &&
                                previewData.sources.length > 0 &&
                                (sourcesExpanded ? (
                                  <ChevronUpIcon className="ml-1 h-4 w-4" />
                                ) : (
                                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                                ))}
                            </div>
                          </Popover.Target>
                          <Popover.Dropdown
                            className={`z-50 mt-3 rounded-xl border border-gray-200 bg-white p-2 hover:border-yellow-500`}
                          >
                            <div style={{ width: tabWidth - 280 }}>
                              <div className="max-h-[450px] overflow-y-auto">
                                {previewData.sources?.map((citation, index) => (
                                  <div
                                    key={`source_${index}`}
                                    className="mb-2 last:mb-0"
                                  >
                                    <RiskReviewSource
                                      isExpanded={true}
                                      sourceIndex={index}
                                      documents={documents}
                                      citation={citation}
                                      citations={previewData.sources ?? []}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Popover.Dropdown>
                        </Popover>
                      </div>
                    </div>
                    <div className="mb-2 whitespace-pre-wrap">
                      {renderAnswerText(
                        previewData.answer_text ?? '',
                        previewData.sources ?? []
                      )}
                    </div>
                    {hasStrikethroughText && (
                      <div className="flex items-center justify-start gap-1 text-gray-400">
                        <ExclamationTriangleIcon className="mb-0.5 h-3 w-3" />
                        <Text size="xs">
                          Some sources in this answer contain struck-through
                          text. Please double-check risk review sources.
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                {/* Default Position Section */}
                {previewData.default_position_met !== null &&
                  previewData.default_position_explanation && (
                    <>
                      <Divider color="#dadada" size="sm" w="100%" />
                      <div className="flex gap-2">
                        <div className="mt-0.5 flex flex-col justify-start">
                          <BriefcaseIcon className="text-brown-400 h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-gray-800">
                              <div className="font-semibold">
                                Default Position
                              </div>
                            </div>
                          </div>
                          <div className="text-md mb-1 flex items-center font-semibold text-gray-900">
                            <div>
                              {previewData.default_position_met
                                ? 'Default Position Met'
                                : 'Default Position Not Met'}
                            </div>
                          </div>
                          <div className="mb-4">
                            <div className="whitespace-pre-wrap">
                              {previewData.default_position_explanation}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                <div className="flex items-center justify-start gap-1 pl-8">
                  <Text c="dimmed" size="sm" className="mb-2">
                    Provision can make mistakes. Consider checking important
                    information.
                  </Text>
                </div>
              </div>
            </div>
          )}

          {!previewData && !isLoading && !error && (
            <div className="py-8 text-center">
              <MagnifyingGlassCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">
                Click "Preview Risk" to see how this risk would be analyzed
              </p>
            </div>
          )}
        </div>
      </Transition>
    </div>
  )
}

export default RiskPreviewPanel
